'use client';

export const CLIENT_SETTINGS_SAVE_TYPE = 'client-settings-save';
export const CLIENT_SETTINGS_SAVE_CHANNEL = 'mrw:client-settings-save';
export const CLIENT_SETTINGS_SAVE_STORAGE_KEY = 'mrw:client-settings-save';

export type ClientSettingsSaveNotification = {
    type: typeof CLIENT_SETTINGS_SAVE_TYPE;
    id: string;
    clientId: string | null;
    version: string;
    sourceInstanceId: string | null;
    occurredAt: string;
    source: 'settings-modal';
};

type ClientSettingsSaveNotificationListener = (notification: ClientSettingsSaveNotification) => void;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const readOptionalString = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim();
    return normalized ? normalized : null;
};

const createNotificationId = (clientId: string | null): string => {
    const suffix =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

    return `settings:${clientId ?? 'unknown'}:${suffix}`;
};

const tryParseJson = (value: string): unknown => {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

export const normalizeClientSettingsSaveNotification = (value: unknown): ClientSettingsSaveNotification | null => {
    if (!isRecord(value) || value.type !== CLIENT_SETTINGS_SAVE_TYPE || value.source !== 'settings-modal') {
        return null;
    }

    const id = readOptionalString(value.id);
    const version = readOptionalString(value.version);
    const occurredAt = readOptionalString(value.occurredAt);
    if (!id || !version || !occurredAt) {
        return null;
    }

    return {
        type: CLIENT_SETTINGS_SAVE_TYPE,
        id,
        clientId: readOptionalString(value.clientId),
        version,
        sourceInstanceId: readOptionalString(value.sourceInstanceId),
        occurredAt,
        source: 'settings-modal',
    };
};

const createBroadcastChannel = (): BroadcastChannel | null => {
    if (typeof BroadcastChannel === 'undefined') {
        return null;
    }

    try {
        return new BroadcastChannel(CLIENT_SETTINGS_SAVE_CHANNEL);
    } catch {
        return null;
    }
};

export const publishClientSettingsSaveNotification = ({
    clientId,
    version,
    sourceInstanceId,
}: {
    clientId: string | null;
    version: string;
    sourceInstanceId?: string | null;
}): void => {
    const notification: ClientSettingsSaveNotification = {
        type: CLIENT_SETTINGS_SAVE_TYPE,
        id: createNotificationId(clientId),
        clientId,
        version,
        sourceInstanceId: sourceInstanceId ?? null,
        occurredAt: new Date().toISOString(),
        source: 'settings-modal',
    };

    const channel = createBroadcastChannel();
    try {
        channel?.postMessage(notification);
    } catch {
        // Best-effort same-browser hint; the server-side version check remains authoritative.
    } finally {
        channel?.close();
    }

    try {
        localStorage.setItem(CLIENT_SETTINGS_SAVE_STORAGE_KEY, JSON.stringify(notification));
        localStorage.removeItem(CLIENT_SETTINGS_SAVE_STORAGE_KEY);
    } catch {
        // Storage can be unavailable. This only disables the proactive hint in sibling tabs.
    }
};

export const subscribeToClientSettingsSaveNotifications = (
    listener: ClientSettingsSaveNotificationListener,
): (() => void) => {
    const handleNotification = (value: unknown) => {
        const notification = normalizeClientSettingsSaveNotification(value);
        if (notification) {
            listener(notification);
        }
    };

    const handleStorage = (event: StorageEvent) => {
        if (event.key !== CLIENT_SETTINGS_SAVE_STORAGE_KEY || !event.newValue) {
            return;
        }

        handleNotification(tryParseJson(event.newValue));
    };

    const channel = createBroadcastChannel();
    const handleChannelMessage = (event: MessageEvent) => handleNotification(event.data);

    window.addEventListener('storage', handleStorage);
    channel?.addEventListener('message', handleChannelMessage);

    return () => {
        window.removeEventListener('storage', handleStorage);
        channel?.removeEventListener('message', handleChannelMessage);
        channel?.close();
    };
};
